<template>
  <div class="vehicle-license-plate-input-container">
    <div
      :class="containerClasses"
      :tabindex="isDisabled ? -1 : 0"
      @click="onToggleDropdown"
      @keydown.enter.prevent="onToggleDropdown"
      @keydown.space.prevent="onToggleDropdown"
      @keydown.esc="onCloseDropdown"
    >
      <input
        ref="inputElement"
        class="vehicle-license-plate-input__input"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :maxlength="limit"
        :value="searchInput"
        @input="onSearchInput"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @click.stop="onOpenDropdown"
      />
      <SpinnerLoader
        v-if="isLoadingVehicleData"
        class="vehicle-license-plate-input__loader"
      />
      <slot v-if="!isLoadingVehicleData" />
    </div>

    <transition name="dropdown">
      <div
        v-if="isDropdownOpen"
        class="vehicle-license-plate-input__dropdown"
        role="listbox"
      >
        <template v-if="foundVehicles.length > 1">
          <div
            v-for="(vehicle, idx) in foundVehicles"
            :key="idx"
            class="vehicle-license-plate-input__item"
            :class="{ 'is-selected': selectedVehicle && selectedVehicle.vin === vehicle.vin }"
            role="option"
            @click="onSelectVehicleFromList(vehicle)"
          >
            <p class="vehicle-license-plate-input__info-text">
              <strong>Марка та модель:</strong> {{ vehicle.mark }} {{ vehicle.model }}
            </p>
            <p class="vehicle-license-plate-input__info-text">
              <strong>Номер кузова (VIN-код):</strong> {{ vehicle.vin }}
            </p>
            <p class="vehicle-license-plate-input__info-text">
              <strong>Рік випуску авто:</strong> {{ vehicle.year }}
            </p>
            <p class="vehicle-license-plate-input__info-text">
              <strong>Місто:</strong> {{ (vehicle.city && vehicle.city.name_full_name_ua) || (vehicle.city && vehicle.city.nameFullNameUa) }}
            </p>
            <p v-if="vehicle.age" class="vehicle-license-plate-input__info-text">
              <strong>Вік власника ТЗ:</strong> {{ vehicle.age }}
            </p>
          </div>
        </template>
        <div
          v-else-if="selectedVehicle"
          class="vehicle-license-plate-input__item"
          role="option"
          @click="onSelectVehicle"
        >
          <p class="vehicle-license-plate-input__info-text">
            <strong>Марка та модель:</strong> {{ vehicleMarkAndModel }}
          </p>
          <p class="vehicle-license-plate-input__info-text">
            <strong>Номер кузова (VIN-код):</strong> {{ vehicleVinCode }}
          </p>
          <p class="vehicle-license-plate-input__info-text">
            <strong>Рік випуску авто:</strong> {{ vehicleYear }}
          </p>
          <p class="vehicle-license-plate-input__info-text">
            <strong>Місто:</strong> {{ vehicleCity }}
          </p>
          <p v-if="vehicleOwnerAge" class="vehicle-license-plate-input__info-text">
            <strong>Вік власника ТЗ:</strong> {{ vehicleOwnerAge }}
          </p>
        </div>
        <div
          v-else
          class="vehicle-license-plate-input__empty-state"
        >
          {{ emptyStateText }}
        </div>
      </div>
    </transition>

    <div
      v-if="hasValidationErrors || errorMessages"
      class="vehicle-license-plate-input__error-container"
      role="alert"
    >
      <div v-for="(error, idx) in validationErrors" :key="idx">
        {{ error }}
      </div>

      <div v-for="(error, idx) in errorMessages" :key="idx">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { vehicleNumberGroups } from '@/features/selectors';
import { SpinnerLoader } from '@/shared/ui/loader';

const VEHICLE_LICENSE_PLATE_SEARCH_DELAY = 1000;
const VALIDATION_ERROR_DISPLAY_DELAY = 3000;
const CYRILLIC_REGEX = /[а-яА-Я]/;
const LATIN_REGEX = /[a-zA-Z]/;

export default {
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Наприклад, АХ1111ВС',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    validVehicleLicensePlateGroups: {
      type: Array,
      default: () => [],
    },
    emptyStateText: {
      type: String,
      default: 'Нічого немає',
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      isInputFocused: false,
      searchInput: '',
      selectedVehicle: null,
      foundVehicles: [],
      searchTimeoutId: null,
      validationErrorTimeoutId: null,
      validationErrors: [],
      isInternalUpdate: false,
    };
  },
  mounted() {
    this.initializeVehicleNumberPatterns();
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    this.clearAllTimeouts();
    document.removeEventListener('click', this.onDocumentClick);
  },
  components: { SpinnerLoader },
  computed: {
    ...mapGetters('vehicleModule', [
      'isLoadingVehicleData',
      'isAutoNumberPatternsReady',
      'getAutoNumberPatterns',
    ]),
    containerClasses() {
      return {
        'vehicle-license-plate-input': true,
        'vehicle-license-plate-input--disabled': this.isDisabled,
        'vehicle-license-plate-input--error': this.hasValidationErrors || this.hasError,
        'vehicle-license-plate-input--open': this.isDropdownOpen,
      };
    },
    hasError() {
      return this.errorMessages?.length > 0;
    },
    hasValidationErrors() {
      return this.validationErrors?.length > 0;
    },
    vehicleMarkAndModel() {
      return `${this.selectedVehicle?.mark} ${this.selectedVehicle?.model}`;
    },
    vehicleVinCode() {
      return this.selectedVehicle?.vin;
    },
    vehicleYear() {
      return this.selectedVehicle?.year;
    },
    vehicleCity() {
      return this.selectedVehicle?.city?.name_full_name_ua || this.selectedVehicle?.city?.nameFullNameUa;
    },
    vehicleOwnerAge() {
      return this.selectedVehicle?.age;
    },
  },
  methods: {
    ...mapActions('vehicleModule', ['getVehicleInfoByNumber', 'fetchAutoNumberPatterns']),
    initializeVehicleNumberPatterns() {
      if (!this.isAutoNumberPatternsReady) {
        this.fetchAutoNumberPatterns();
      }
    },
    isVehicleNumberValid(vehicleNumber, patternGroups = []) {
      if (!vehicleNumber || typeof vehicleNumber !== 'string') {
        return false;
      }

      const UACarLetters = 'A-ZАВЕІКМНОРСТХ';

      const enhancePattern = (patternStr) => {
        return patternStr.replace(/\[A-Z\]/g, `[${UACarLetters}]`);
      };

      const groupsToSearch = patternGroups?.length > 0 ? patternGroups : vehicleNumberGroups.default;
      const patternList = this.getAutoNumberPatterns?.filter(p => groupsToSearch.includes(p?.group)) || [];

      for (const patternObj of patternList) {
        const enhancedPattern = enhancePattern(patternObj?.pattern);
        const regex = new RegExp(enhancedPattern);
        if (regex.test(vehicleNumber.toUpperCase())) {
          return true;
        }
      }

      return false;
    },
    validateVehicleData() {
      if (this.foundVehicles.length > 1) return;
      if (!this.selectedVehicle?.city && !this.selectedVehicle?.mark && !this.selectedVehicle?.model
        && !this.selectedVehicle?.number && !this.selectedVehicle?.type && !this.selectedVehicle?.vin
        && !this.selectedVehicle?.year) {

        this.selectedVehicle = null;
      }
    },
    onToggleDropdown() {
      if (this.isDisabled || this.isLoading) return;
      this.isDropdownOpen ? this.onCloseDropdown() : this.onOpenDropdown();
    },
    onOpenDropdown() {
      if (this.isDisabled || this.hasValidationErrors) return;
      if (!this.selectedVehicle && !this.foundVehicles.length && !this.searchInput) return;
      this.isDropdownOpen = true;
      this.$nextTick(() => {
        this.$refs.inputElement?.focus();
        this.isInputFocused = true;
      });
    },
    onCloseDropdown() {
      this.isDropdownOpen = false;
    },
    onSearchInput(event) {
      const rawValue = event.target.value || '';
      this.searchInput = rawValue.trim().toUpperCase();
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.onCloseDropdown();
      }
    },
    onInputFocus() {
      this.isInputFocused = true;
    },
    onInputBlur() {
      this.isInputFocused = false;
      this.validationErrors = [];
      this.onCloseDropdown();
      this.$emit('blur');
    },
    onSelectVehicle() {
      this.validationErrors = [];
      this.onCloseDropdown();
      this.$emit('input', this.selectedVehicle?.number || null);
      this.$emit('change', this.selectedVehicle);
    },
    onSelectVehicleFromList(vehicle) {
      this.selectedVehicle = vehicle;
      this.validationErrors = [];
      this.onCloseDropdown();
      this.$emit('input', vehicle?.number || null);
      this.$emit('change', vehicle);
    },
    clearAllTimeouts() {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
        this.searchTimeoutId = null;
      }
      if (this.validationErrorTimeoutId) {
        clearTimeout(this.validationErrorTimeoutId);
        this.validationErrorTimeoutId = null;
      }
    },
    hasMultipleScriptLayouts(input) {
      return LATIN_REGEX.test(input) && CYRILLIC_REGEX.test(input);
    },
    getValidationErrors(input) {
      const errors = [];

      if (this.isAutoNumberPatternsReady && !this.isVehicleNumberValid(input, this.validVehicleLicensePlateGroups)) {
        errors.push('Введіть коректно формат номеру авто');
      }

      if (this.hasMultipleScriptLayouts(input)) {
        errors.push('Введіть номерний знак тільки латиницею або тільки кирилицею');
      }

      return errors;
    },
    scheduleValidationErrorDisplay() {
      if (this.validationErrorTimeoutId) {
        clearTimeout(this.validationErrorTimeoutId);
      }

      this.validationErrorTimeoutId = setTimeout(() => {
        this.validationErrorTimeoutId = null;

        const currentErrors = this.getValidationErrors(this.searchInput);

        if (currentErrors.length > 0) {
          this.onCloseDropdown();
          this.validationErrors = currentErrors;
        }
      }, VALIDATION_ERROR_DISPLAY_DELAY);
    },
    scheduleVehicleNumberSearch(input) {
      this.clearAllTimeouts();

      if (input.length > 0) {
        const errors = this.getValidationErrors(input);

        if (errors.length > 0) {
          this.scheduleValidationErrorDisplay();
          return;
        }
      }

      this.searchTimeoutId = setTimeout(async () => {
        if (!this.isVehicleNumberValid(input, this.validVehicleLicensePlateGroups)
          || this.hasMultipleScriptLayouts(input)) {
          return;
        }

        this.onCloseDropdown();
        const result = await this.getVehicleInfoByNumber(input);
        const vehicles = result?.vehicles || [];
        this.foundVehicles = vehicles;

        if (vehicles.length === 1) {
          this.selectedVehicle = vehicles[0];
          this.validateVehicleData();
        } else if (vehicles.length > 1) {
          this.selectedVehicle = null;
          this.onOpenDropdown();
        } else {
          this.selectedVehicle = null;
        }

        this.searchTimeoutId = null;

        this.$emit('blur');
      }, VEHICLE_LICENSE_PLATE_SEARCH_DELAY);
    },
    clearInput() {
      this.clearAllTimeouts();
      this.selectedVehicle = null;
      this.foundVehicles = [];
      this.validationErrors = [];
      this.onCloseDropdown();
      this.isInternalUpdate = true;
      this.$emit('input', null);
      this.$emit('change', null);
      this.$nextTick(() => {
        this.isInternalUpdate = false;
      });
    },
  },
  watch: {
    value: {
      handler(newValue, oldValue) {
        if (this.isInternalUpdate) return;

        if (newValue === null || newValue === undefined || newValue === '') {
          this.searchInput = '';
          this.selectedVehicle = null;
          this.foundVehicles = [];
          this.validationErrors = [];
          this.clearAllTimeouts();
          return;
        }

        if (newValue !== oldValue) {
          const number = typeof newValue === 'object' ? newValue?.number : newValue;
          if (number && this.selectedVehicle?.number !== number) {
            this.searchInput = number;
            this.scheduleVehicleNumberSearch(number);
          }
        }
      },
      immediate: true,
    },
    searchInput(newValue, oldValue) {
      if (newValue === oldValue) return;

      this.clearAllTimeouts();
      this.validationErrors = [];

      if (!newValue) {
        this.clearInput();
        return;
      }

      this.selectedVehicle = null;
      this.foundVehicles = [];
      this.scheduleVehicleNumberSearch(newValue);
    },
    selectedVehicle(newValue) {
      this.$emit('revisedVehicleData', newValue);

      if (newValue && this.isInputFocused && !this.hasValidationErrors && !this.isDropdownOpen && this.foundVehicles.length <= 1) {
        this.onOpenDropdown();
      }
    },
    isAutoNumberPatternsReady(isReady) {
      if (isReady && this.searchInput) {
        this.scheduleVehicleNumberSearch(this.searchInput);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.vehicle-license-plate-input-container {
  position: relative;
  width: 100%;
}

.vehicle-license-plate-input {
  position: relative;
  background-color: #ffffff;
  border: 1px solid #E4E4E7;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--disabled {
    background-color: #F6F6F9;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--error {
    border-color: #ef4444;
  }

  &__input {
    font-family: inherit;
    font-size: 14px;
    color: #09090B;
    background: transparent;
    border: none;
    outline: none;
    flex: 1;
    cursor: text;
    min-width: 0;

    &::placeholder {
      color: #D4D4D8;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__loader {
    width: 20px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 300px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    margin-top: 4px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &__item {
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: #09090B;

    &:hover,
    &.is-focused {
      background-color: #f3f4f6;
    }

    &.is-selected {
      background-color: #eff6ff;
      color: #FB923C;
    }
  }

  &__empty-state {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  }

  &__info-text {
    margin: 0;

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    strong {
      font-weight: 600;
    }
  }

  &__error-container {
    font-size: 12px;
    color: #ef4444;
    margin-top: 4px;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
